import {
    NEW_TAB_HEAD,
    DASH_NEW_HEADER,
    CONSULTANT_NEW_ATTENDED_HEAD,
    ATTENDED_CHECKLISTS_TAB_HEAD,
} from './newLead';

// Returns sub-tab options for the template builder's sub-tab picker.
// NEW merges the lead-table tabs with dashboard-only tabs (NHS, AWAITING_RESPONSE flagged).
// Returns [] for statuses with no sub-tabs so the picker can be hidden.
// Pass customTabs (placement='top' tabs for this status's parent table) to include them.
export function getSubTabsForStatus(status, customTabs = []) {
    const customEntries = customTabs
        .filter((t) => !t.is_hidden)
        .map((t) => ({ value: `custom:${t.id}`, label: t.name, isCustomTab: true }));
    if (status === 'NEW') {
        const baseValues = new Set(NEW_TAB_HEAD.map((t) => t.value));
        const dashboardExtra = DASH_NEW_HEADER
            .filter((t) => !baseValues.has(t.value))
            .map((t) => ({ ...t, dashboardOnly: true }));
        return [...NEW_TAB_HEAD, ...dashboardExtra, ...customEntries];
    }
    if (status === 'CONSULTATION_ATTENDED') {
        return [...CONSULTANT_NEW_ATTENDED_HEAD, ...ATTENDED_CHECKLISTS_TAB_HEAD, ...customEntries];
    }
    return [...customEntries];
}

// Cross-component signal: dispatched on `window` whenever a tasks-v2 checklist item
// is mutated (completed / skipped / paused / resumed / reopened / rescheduled) so
// detached listeners — e.g. the attended-table's per-task date columns
// (useTaskChecklistColumns) — can refetch without prop-drilling a refresh callback
// through the V1 selectedRow popup flow. Emit via window.dispatchEvent(new Event(...)).
export const TASKS_V2_CHANGED_EVENT = 'tasks-v2:changed';

// Completion states for a checklist item. Must mirror BE ChecklistCompletionState enum.
export const CHECKLIST_COMPLETION_STATE = {
    PENDING: 'PENDING',
    YES: 'YES',
    PAUSED: 'PAUSED',
    SKIPPED: 'SKIPPED',
};

// Deprecated checklist item types — DO NOT remove from this object or from CHECKLIST_ITEM_TYPE_LABELS.
// Constants remain so existing items still load and render via their cell handlers.
// Use LEAD_NEXT_ACTION (status-aware) for all new template items.
//
// Wave 1 (2026-05-14): PHONE_CALLED, OPEN_FOLLOW_UP
//   — duplicates of LEAD_NEXT_ACTION; render via OpenFollowUpCell.
// Wave 2 (2026-05-14): RECORD_ATTENDANCE, RECORD_FAILED_TO_ATTEND, START_TREATMENT,
//   COMPLETE_TREATMENT, LEAD_STATUS_CHANGED
//   — lead-transition shortcuts now covered by LEAD_NEXT_ACTION's status-aware menu;
//     LEAD_STATUS_CHANGED deprecated with explicit human sign-off (archive via lead-detail popup).

// ChecklistItem action types. Must mirror BE ChecklistItemType enum.
export const CHECKLIST_ITEM_TYPE = {
    CONFIRMATION: 'CONFIRMATION',
    EMAIL_SENT: 'EMAIL_SENT',
    WHATSAPP_SENT: 'WHATSAPP_SENT',
    SMS_SENT: 'SMS_SENT',
    PHONE_CALLED: 'PHONE_CALLED',
    FREE_TEXT: 'FREE_TEXT',
    MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
    LOG_PHONE_CALL: 'LOG_PHONE_CALL',
    SEND_CONSENT_FORM: 'SEND_CONSENT_FORM',
    SEND_REVIEW_REQUEST: 'SEND_REVIEW_REQUEST',
    SEND_PAYMENT_LINK: 'SEND_PAYMENT_LINK',
    ADD_NOTE: 'ADD_NOTE',
    SCHEDULE_REMINDER: 'SCHEDULE_REMINDER',
    OPEN_FOLLOW_UP: 'OPEN_FOLLOW_UP',
    BOOK_APPOINTMENT: 'BOOK_APPOINTMENT',
    RECORD_ATTENDANCE: 'RECORD_ATTENDANCE',
    RECORD_FAILED_TO_ATTEND: 'RECORD_FAILED_TO_ATTEND',
    START_TREATMENT: 'START_TREATMENT',
    COMPLETE_TREATMENT: 'COMPLETE_TREATMENT',
    SEND_TREATMENT_PLAN: 'SEND_TREATMENT_PLAN',
    UPLOAD_DOCUMENT: 'UPLOAD_DOCUMENT',
    LINKED_CHECKLIST: 'LINKED_CHECKLIST',
    LEAD_STATUS_CHANGED: 'LEAD_STATUS_CHANGED',
    LEAD_NEXT_ACTION: 'LEAD_NEXT_ACTION',
    // #273 — New structured-data types for SYSTEM templates (BE Wave 1)
    YES_NO_TOGGLE: 'YES_NO_TOGGLE',
    CURRENCY_AMOUNT: 'CURRENCY_AMOUNT',
    PAYMENT_METHOD: 'PAYMENT_METHOD',
    STAFF_PICKER: 'STAFF_PICKER',
    DATE_FIELD: 'DATE_FIELD',
    SIMPLE_DATE: 'SIMPLE_DATE',
};

// Wave 3 (2026-05-16) — make the deprecation list machine-readable so the
// pickers can hide them programmatically and the cell-handler audit table
// can flag any new deprecation without a comment-only contract.
// Items here will NOT appear in ChecklistMode.jsx or TaskSlideoutChecklist.jsx
// pickers; existing items with these types continue to render via their
// cell handlers (kept intact for backwards compatibility).
export const DEPRECATED_CHECKLIST_ITEM_TYPES = new Set([
    // Wave 1 (2026-05-14) — duplicates of LEAD_NEXT_ACTION
    'PHONE_CALLED',
    'OPEN_FOLLOW_UP',
    // Wave 2 (2026-05-14) — lead-transition shortcuts subsumed by status-aware
    // LEAD_NEXT_ACTION menu; LEAD_STATUS_CHANGED archive flow moved to lead-detail popup
    'RECORD_ATTENDANCE',
    'RECORD_FAILED_TO_ATTEND',
    'START_TREATMENT',
    'COMPLETE_TREATMENT',
    'LEAD_STATUS_CHANGED',
]);

// Action types that count as an outbound "contact attempt" on a lead.
// Consumed by the lead-detail "Contact attempts" pill counter — when a
// checklist item of one of these types completes (YES / is_completed),
// it bumps the pill by 1 alongside the legacy V1 Note-based count.
//
// Includes deprecated types (PHONE_CALLED) because existing items still
// load and complete via their cell handlers — historical attempts should
// keep counting. LEAD_NEXT_ACTION is included unconditionally per
// product call (any resolved_action counts; reasoning: completing a
// next-action menu always represents the user acting on the lead).
//
// When a new contact-style checklist type ships, add it to this Set.
// Single source of truth — every consumer derives from here so the
// count stays consistent across surfaces.
export const CONTACT_ATTEMPT_CHECKLIST_TYPES = new Set([
    'PHONE_CALLED',
    'LOG_PHONE_CALL',
    'WHATSAPP_SENT',
    'EMAIL_SENT',
    'SMS_SENT',
    'LEAD_NEXT_ACTION',
]);

// Checklist item types that are TOUCHPOINTS (contact attempts / scheduled
// follow-up reminders). They do NOT represent blocking clinical or admin work.
//
// A WORK item = any item whose type is NOT in this set. Unknown/new types
// default to WORK (safe: an unrecognised type never falsely unlocks "complete").
//
// Used by computeItemSummary() to discriminate "Checklist complete · follow-up
// paused until D MMM" (all work done, only touchpoints paused) from "Checklist
// paused" (at least one WORK item is PAUSED — lead genuinely on hold).
//
// TOUCHPOINT types (do not block completion):
//   Contact-attempt channels: LEAD_NEXT_ACTION, CONFIRMATION, LOG_PHONE_CALL,
//   EMAIL_SENT, WHATSAPP_SENT, SMS_SENT, PHONE_CALLED (deprecated Wave 1).
//   Reminder scheduling: SCHEDULE_REMINDER, OPEN_FOLLOW_UP (deprecated Wave 1).
//
// WORK types (block completion if PAUSED or PENDING):
//   Clinical/structured data: YES_NO_TOGGLE, PAYMENT_METHOD, SIMPLE_DATE,
//   DATE_FIELD, STAFF_PICKER, CURRENCY_AMOUNT.
//   Actions: SEND_CONSENT_FORM, SEND_PAYMENT_LINK, SEND_TREATMENT_PLAN,
//   UPLOAD_DOCUMENT, BOOK_APPOINTMENT, ADD_NOTE, SEND_REVIEW_REQUEST,
//   FREE_TEXT, MULTIPLE_CHOICE, LINKED_CHECKLIST.
//   Deprecated but still blocking: START_TREATMENT, COMPLETE_TREATMENT,
//   RECORD_ATTENDANCE, RECORD_FAILED_TO_ATTEND, LEAD_STATUS_CHANGED.
//   Unknown types also default to WORK.
export const TOUCHPOINT_CHECKLIST_ITEM_TYPES = new Set([
    'LEAD_NEXT_ACTION',  // main modern follow-up / next-action type
    'CONFIRMATION',      // contact touchpoint (e.g. "Open Follow Up — set custom date")
    'LOG_PHONE_CALL',    // logging a phone call
    'EMAIL_SENT',        // outbound email contact
    'WHATSAPP_SENT',     // outbound WhatsApp contact
    'SMS_SENT',          // outbound SMS contact
    'PHONE_CALLED',      // deprecated Wave 1 alias for LOG_PHONE_CALL
    'SCHEDULE_REMINDER', // scheduling a future reminder
    'OPEN_FOLLOW_UP',    // deprecated Wave 1 alias for LEAD_NEXT_ACTION
]);

/**
 * Analyses a single task's `checklist_items` array and returns the signals
 * needed to compute allWorkDone and hasPausedTouchpoints at treatment level.
 *
 * Call once per task; aggregate across tasks with OR (hasPausedWork,
 * hasPausedTouchpoints) and sum (_workCount, _workDoneCount).
 *
 * Treatment-level allWorkDone = _workCount > 0 && !hasPausedWork
 *   && _workDoneCount === _workCount
 *
 * @param {Array<{item_type: string, completion_state: string}>} items
 * @returns {{ hasPausedWork: boolean, hasPausedTouchpoints: boolean, workCount: number, workDoneCount: number }}
 */
export function computeItemSummary(items) {
    const result = { hasPausedWork: false, hasPausedTouchpoints: false, workCount: 0, workDoneCount: 0 };
    items.forEach((item) => {
        const isTouchpoint = TOUCHPOINT_CHECKLIST_ITEM_TYPES.has(item.item_type);
        if (isTouchpoint) {
            if (item.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED) {
                result.hasPausedTouchpoints = true;
            }
        } else {
            result.workCount += 1;
            if (item.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED) {
                result.hasPausedWork = true;
            } else if (
                item.completion_state === CHECKLIST_COMPLETION_STATE.YES ||
                item.completion_state === CHECKLIST_COMPLETION_STATE.SKIPPED
            ) {
                result.workDoneCount += 1;
            }
            // PENDING: counted in workCount but not workDoneCount → allWorkDone = false
        }
    });
    return result;
}

export const CHECKLIST_ITEM_TYPE_LABELS = {
    CONFIRMATION: 'Confirmation (checkbox)',
    EMAIL_SENT: 'Email sent',
    WHATSAPP_SENT: 'WhatsApp sent',
    SMS_SENT: 'SMS sent',
    PHONE_CALLED: 'Phone call made',
    FREE_TEXT: 'Free text response',
    MULTIPLE_CHOICE: 'Multiple choice',
    LOG_PHONE_CALL: 'Log phone call',
    SEND_CONSENT_FORM: 'Send consent form',
    SEND_REVIEW_REQUEST: 'Send review request',
    SEND_PAYMENT_LINK: 'Send payment link',
    ADD_NOTE: 'Add note',
    SCHEDULE_REMINDER: 'Schedule reminder',
    OPEN_FOLLOW_UP: 'Open follow-up',
    BOOK_APPOINTMENT: 'Book appointment',
    RECORD_ATTENDANCE: 'Record attendance',
    RECORD_FAILED_TO_ATTEND: 'Record failed to attend',
    START_TREATMENT: 'Start treatment',
    COMPLETE_TREATMENT: 'Complete treatment',
    SEND_TREATMENT_PLAN: 'Send treatment plan',
    UPLOAD_DOCUMENT: 'Upload document',
    LINKED_CHECKLIST: 'Linked checklist',
    LEAD_STATUS_CHANGED: 'Change lead status',
    LEAD_NEXT_ACTION: 'Next action (dynamic)',
    YES_NO_TOGGLE: 'Yes / No toggle',
    CURRENCY_AMOUNT: 'Currency amount (£)',
    PAYMENT_METHOD: 'Payment method',
    STAFF_PICKER: 'Staff member picker',
    DATE_FIELD: 'Date field',
    SIMPLE_DATE: 'Date field simple',
};

// How a template gets instantiated as a Task for a lead. Must mirror BE TaskInstantiationMode enum.
export const INSTANTIATION_MODE = {
    TRIGGER_ONLY: 'TRIGGER_ONLY',
    ON_CLICK: 'ON_CLICK',
    MANUAL: 'MANUAL',
    AUTO_APPLY_TO_STATUS: 'AUTO_APPLY_TO_STATUS',
};

// Maps sub-tab tableTitle values to the parent TABLE_NAMES entry used for ?table= queries.
// Sub-tabs share the parent table's checklist column configuration.
export const SUB_TAB_TO_PARENT_TABLE = {
    'Attended Checklists': 'Consultation Attended Leads',
    'Consultation ClinCheck Leads': 'Consultation Attended Leads',
    // FE renders this page as "Newest Leads" but the BE canonical value is "New Leads"
    'Newest Leads': 'New Leads',
    // NHSLeadTable is a filtered New Leads surface; the BE table enum does not
    // accept "NHS Leads" for checklist-column lookups.
    'NHS Leads': 'New Leads',
    // /dashboard/tables/all-lead-table uses tableTitle 'All Leads' for FE prefs but
    // BE's TaskColumnTable enum (apps/tasks/choices.py) doesn't include it — remap
    // to 'Newest Leads' so V2 columns render (BE returns 400 otherwise).
    'All Leads': 'Newest Leads',
    'Category Summary Table': 'Newest Leads',
};

// Lead statuses that can be selected in the template "Applies to" picker.
// Values must match BE TemplateLeadStatus (apps/tasks/choices.py) — this enum is shared
// by both tasks-v2 TaskTemplate and engagement-template serializers.
// NOTE: the canonical value is FAILED_TO_ATTEND (not FAILED_TO_ATTEND_CONSULTATION —
// that longer form lives in apps.commons.choices and is NOT used for template targeting).
export const TEMPLATE_STATUS_OPTIONS = [
    { value: 'NEW', label: 'New Leads' },
    { value: 'CONSULTATION_BOOKED', label: 'Consultation Booked' },
    { value: 'CONSULTATION_ATTENDED', label: 'Consultation Attended' },
    { value: 'FAILED_TO_ATTEND', label: 'Failed to Attend' },
    { value: 'TREATMENT_STARTED', label: 'Treatment Started' },
    { value: 'CLOSED', label: 'Completed' },
    { value: 'ARCHIVED', label: 'Archived' },
];

// Statuses → table name caption shown under the "Applies to" picker.
export const STATUS_TO_TABLE_LABEL = {
    NEW: 'New Leads',
    CONSULTATION_BOOKED: 'Consultation Booked Leads',
    CONSULTATION_ATTENDED: 'Consultation Attended Leads',
    FAILED_TO_ATTEND: 'Failed to attend Leads',
    TREATMENT_STARTED: 'Treatment Started Leads',
    CLOSED: 'Completed Leads',
    ARCHIVED: 'Archived Leads',
};

// Compatibility matrix: for each due_anchor, the set of status values where it is INCOMPATIBLE.
// Used for inline validation and save-button gating.
export const ANCHOR_INCOMPATIBLE_STATUSES = {
    TEMPLATE_APPLIED: new Set(),
    LEAD_CREATED: new Set(),
    APPOINTMENT: new Set(['NEW', 'ARCHIVED']),
    ATTENDED: new Set(['NEW', 'CONSULTATION_BOOKED', 'FAILED_TO_ATTEND', 'ARCHIVED']),
    // Only valid on templates targeting TREATMENT_STARTED (or CLOSED) — anywhere
    // else there's no treatment-started transition to anchor on. Mirrors the BE
    // validators._INCOMPATIBLE[DueAnchor.TREATMENT_STARTED].
    TREATMENT_STARTED: new Set(['NEW', 'CONSULTATION_BOOKED', 'CONSULTATION_ATTENDED', 'FAILED_TO_ATTEND', 'ARCHIVED']),
};

// Human-readable anchor labels for error messages.
export const ANCHOR_LABELS = {
    TEMPLATE_APPLIED: 'Template Applied',
    LEAD_CREATED: 'Lead Created',
    APPOINTMENT: 'Appointment Date',
    ATTENDED: 'Attended Date',
    TREATMENT_STARTED: 'Treatment Started Date',
};

// ── Lead-status → treatment-status translation ──────────────────────────────
// The FE lead-status filter uses `FAILED_TO_ATTEND` (matching the template
// enum in apps/tasks/choices.py: TemplateLeadStatus), but the treatment model
// uses `FAILED_TO_ATTEND_CONSULTATION` (apps/commons/choices.py:
// LeadTreatmentStatus).  All other status values are identical in both enums.
// This helper translates an array of FE status values to the corresponding
// BE treatment statuses before sending `lead_treatment__status__in`.
// DO NOT apply to TEMPLATE_STATUS_OPTIONS or any template-targeting enum —
// templates legitimately use the short `FAILED_TO_ATTEND` form.
export function mapLeadStatusesToTreatmentStatuses(values) {
    if (!Array.isArray(values)) return values;
    return values.map((v) => (v === 'FAILED_TO_ATTEND' ? 'FAILED_TO_ATTEND_CONSULTATION' : v));
}
